package com.sddamico.components.motion

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Animation
import com.varabyte.kobweb.compose.css.PointerEvents
import com.varabyte.kobweb.compose.css.Visibility
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.theme.name
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.withTimeoutOrNull
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement
import org.w3c.dom.asList
import kotlin.js.json

/**
 * How long the outgoing page stays up while the incoming one loads. Past this, a skeleton takes over so a slow
 * request never looks like a click that did nothing.
 */
private const val HOLD_MAX_MILLIS = 450L

private const val GHOST_EXIT_MILLIS = 120

val PageItemEnterKeyframes = Keyframes {
    from { Modifier.opacity(0).translateY(10.px) }
    to { Modifier.opacity(1).translateY(0.px) }
}

val FadeInKeyframes = Keyframes {
    from { Modifier.opacity(0) }
    to { Modifier.opacity(1) }
}

/**
 * The page's direct child fades in; its children (the page's sections) rise in on a short stagger. Rules key off
 * `data-revealed`, so the entrance plays when a page is revealed after loading, and any section that mounts later
 * (an error card, a list that finished loading) gets the same entrance on insertion.
 *
 * `backwards` fill only: a transform left in place after the animation would make each section the containing block
 * for `position: fixed` descendants such as modals.
 */
val PageLayerStyle = CssStyle {
    cssRule("[data-holding]") {
        Modifier.visibility(Visibility.Hidden).pointerEvents(PointerEvents.None)
    }
    cssRule("[data-revealed] > *") {
        Modifier.animation(
            FadeInKeyframes.toAnimation(colorMode, 200.ms, Ease.Out.fn, fillMode = AnimationFillMode.Backwards)
        )
    }
    cssRule("[data-revealed] > * > *") {
        Modifier.animation(
            PageItemEnterKeyframes.toAnimation(
                colorMode, Durations.ENTER.ms, Ease.Out.fn, fillMode = AnimationFillMode.Backwards
            )
        )
    }
    (2..6).forEach { index ->
        cssRule("[data-revealed] > * > *:nth-child($index)") {
            Modifier.animation { delay(((index - 1) * Durations.STAGGER_STEP).ms) }
        }
    }
    cssRule("[data-revealed] > * > *:nth-child(n+7)") {
        Modifier.animation { delay((6 * Durations.STAGGER_STEP).ms) }
    }
    cssRule("[data-revealed] > [data-motion-continue] > *") {
        Modifier.animation(Animation.None)
    }
    cssRule(ReducedMotionQuery, "[data-revealed] > * > *") {
        Modifier.animation { name(FadeInKeyframes.name) }
    }
}

@Stable
class PageTransitionState internal constructor() {
    internal var pendingLoads by mutableStateOf(0)
    internal var lastHoldEndedAt = 0.0
}

val LocalPageTransition = staticCompositionLocalOf<PageTransitionState?> { null }

/**
 * Marks the current page as still loading. While any page content is pending, the previous page stays on screen
 * (see [PageTransitionHost]).
 */
@Composable
fun HoldPageTransition() {
    val state = LocalPageTransition.current ?: return
    DisposableEffect(state) {
        state.pendingLoads++
        onDispose {
            state.pendingLoads--
            state.lastHoldEndedAt = window.performance.now()
        }
    }
}

/**
 * True when another loading placeholder was on screen a moment ago, e.g. the layout's auth placeholder handing over
 * to the page's own. The new placeholder should then appear as a continuation rather than replay its entrance.
 */
@Composable
fun rememberContinuesLoading(): Boolean {
    val state = LocalPageTransition.current ?: return false
    // The outgoing placeholder is disposed after this composes, so it still counts as pending here.
    return remember { state.pendingLoads > 0 || window.performance.now() - state.lastHoldEndedAt < 100 }
}

private class Navigation(val ghost: HTMLElement?) {
    var holding by mutableStateOf(ghost != null)
}

private class HostElements {
    var host: HTMLElement? = null
    var pageLayer: HTMLElement? = null
    var ghostLayer: HTMLElement? = null
    var ghost: HTMLElement? = null
    var current: Navigation? = null

    /**
     * Runs during composition, before the incoming page replaces the outgoing one in the DOM, so the snapshot is of
     * the page the user was just looking at.
     */
    fun captureGhost(): HTMLElement? {
        val pageLayer = pageLayer ?: return null
        val previousGhost = ghost
        // A second navigation while the first is still loading keeps showing the page the user actually saw.
        if (previousGhost != null && current?.holding == true) return previousGhost
        previousGhost?.remove()

        val clone = pageLayer.cloneNode(deep = true) as HTMLElement
        clone.removeAttribute("class")
        clone.removeAttribute("data-revealed")
        clone.querySelectorAll("[id]").asList().forEach { (it as HTMLElement).removeAttribute("id") }
        clone.setAttribute("aria-hidden", "true")
        clone.setAttribute("inert", "")
        ghost = clone
        return clone
    }

    fun clearGhost() {
        ghost?.remove()
        ghost = null
        host?.style?.removeProperty("min-height")
    }
}

/**
 * Hosts the routed page and animates between pages.
 *
 * On navigation the outgoing page is snapshotted and left in place, dimmed, while the incoming page loads; once
 * nothing on the new page is pending (or [HOLD_MAX_MILLIS] passes) the snapshot fades out and the new page's sections
 * rise in. This replaces the old swap-to-"Loading..."-then-pop with a single continuous transition.
 */
@Composable
fun PageTransitionHost(routeKey: String, content: @Composable () -> Unit) {
    val state = remember { PageTransitionState() }
    val elements = remember { HostElements() }
    val navigation = remember(routeKey) { Navigation(elements.captureGhost()) }

    DisposableEffect(navigation) {
        elements.current = navigation
        val ghost = navigation.ghost
        if (ghost != null && ghost.parentElement == null) {
            elements.ghostLayer?.appendChild(ghost)
            elements.host?.style?.setProperty("min-height", "${ghost.offsetHeight}px")
        }
        onDispose { }
    }

    LaunchedEffect(navigation) {
        val ghost = navigation.ghost ?: return@LaunchedEffect
        // A snapshot reused by a second navigation is already part-way dimmed.
        val dimFrom = window.getComputedStyle(ghost).opacity
        (ghost.asDynamic().getAnimations() as Array<dynamic>).forEach { it.cancel() }
        val dim = ghost.asDynamic().animate(
            arrayOf(json("opacity" to dimFrom), json("opacity" to 0.55)),
            json("duration" to 200, "delay" to 80, "easing" to Ease.Out.css, "fill" to "both"),
        )
        withTimeoutOrNull(HOLD_MAX_MILLIS) {
            snapshotFlow { state.pendingLoads }.first { it == 0 }
        }
        navigation.holding = false

        val currentOpacity = window.getComputedStyle(ghost).opacity
        dim.cancel()
        // A slight blur on the way out keeps the two pages from reading as a double exposure while they overlap.
        ghost.asDynamic().animate(
            arrayOf(json("opacity" to currentOpacity, "filter" to "blur(0px)"), json("opacity" to 0, "filter" to "blur(2px)")),
            json("duration" to GHOST_EXIT_MILLIS, "easing" to Ease.Out.css, "fill" to "forwards"),
        )
        delay(GHOST_EXIT_MILLIS.toLong())
        elements.clearGhost()
    }

    DisposableEffect(Unit) {
        onDispose { elements.clearGhost() }
    }

    Div(
        Modifier
            .position(Position.Relative)
            .fillMaxWidth()
            .toAttrs {
                ref { element ->
                    elements.host = element
                    onDispose { elements.host = null }
                }
            }
    ) {
        // Holds only the snapshot, which is plain DOM. Compose never manages children here, so appending to it
        // can't disturb the composition's view of the tree.
        Div(
            Modifier
                .position(Position.Absolute)
                .top(0.px).left(0.px).right(0.px)
                .pointerEvents(PointerEvents.None)
                .zIndex(1)
                .toAttrs {
                    ref { element ->
                        elements.ghostLayer = element
                        onDispose { elements.ghostLayer = null }
                    }
                }
        )
        Div(
            PageLayerStyle.toAttrs {
                if (navigation.holding) attr("data-holding", "") else attr("data-revealed", "")
                ref { element ->
                    elements.pageLayer = element
                    onDispose { elements.pageLayer = null }
                }
            }
        ) {
            CompositionLocalProvider(LocalPageTransition provides state) {
                content()
            }
        }
    }
}
